/**
 * FieldError Component
 *
 * A wrapper around Formik's ErrorMessage that provides a library-agnostic API.
 * Displays validation error messages for a field.
 *
 * @example
 * ```typescript
 * import { FieldError } from '@/forms/core';
 *
 * <FieldError name="email" />
 * ```
 */

import React from 'react';
import { ErrorMessage } from 'formik';

import { FieldErrorMessage } from './FieldErrorMessage';

import './FieldError.scss';

export interface FieldErrorProps {
  /** Field name to show error for */
  name: string;
  /** CSS class name applied to the error row wrapper */
  className?: string;
  /** @deprecated Use the default layout; kept for backward compatibility */
  component?: React.ElementType;
  /** Custom render function */
  children?: (message: string) => React.ReactNode;
}

/**
 * Component to display field validation errors
 */
export const FieldError: React.FC<FieldErrorProps> = ({
  name,
  className,
  children,
}) => {
  if (children) {
    return (
      <ErrorMessage name={name}>
        {message => (message ? children(message) : null)}
      </ErrorMessage>
    );
  }

  return (
    <ErrorMessage name={name}>
      {message => (
        <FieldErrorMessage message={message} className={className} />
      )}
    </ErrorMessage>
  );
};
